import { Injectable } from '@angular/core';
import { EMPTY, BehaviorSubject, ReplaySubject, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { tap, map, catchError } from 'rxjs/operators';
import { RestAPIService } from 'app/services/rest-api.service';
import { DataService } from 'app/shared/data/data.service';
import { GetModelsListInterface, GetModelByIdInterface, Label, ComponentsByEntityId, TagDetail, PropertiesInterface, GetModelById } from 'app/services/graphqlApi.service';
import { getModelById, getModelByIdForDashboards, getModelsList, getModelTypes } from 'app/shared/constants/gql';
import { IModel, ModelComponent, ModelDetails } from './../interfaces/model';
import { getComponentInstAlarms } from 'app/utils/alarms';
import { IAddRefTypes, IModelType } from 'app/shared/interfaces/model';
import { RestRequestType } from 'app/shared/enums/rest-request-type.enum';
import { EEntityTypes } from 'app/shared/enums';

import { Apollo } from 'apollo-angular';
import { ApolloQueryResult } from 'apollo-client';

interface Property {
  name: string;
  value: string;
  ataTypeId: string;
}

export interface ModelResponse {
  id: string;
  name: string;
  labels: string[];
  components: string[];
  properties?: Property[];
  tags?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class ModelsService {
  readonly _modelsSource = new BehaviorSubject<ModelDetails[]>([]);
  readonly models$ = this._modelsSource.asObservable();

  private _modelDetails = new BehaviorSubject<ModelDetails>({} as ModelDetails);
  readonly modelDetails$ = this._modelDetails.asObservable();

  readonly _modelTypes = new BehaviorSubject<IModelType[]>([]);
  readonly modelTypes$ = this._modelTypes.asObservable();

  constructor(
    private apollo: Apollo,
    private apiService: RestAPIService,
    private toastr: ToastrService,
    readonly dataService: DataService) { }

  createModel(name: string, components: ModelComponent[] = [], labels?: string[], modelTypeId?: number, notes?: string, hasSystemTags?: boolean): Observable<ModelResponse> {
    return this.apiService.post<ModelResponse>(EEntityTypes.MODELS, RestRequestType.CREATE_MODEL, { name, labels, components, modelTypeId, notes, hasSystemTags })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
        if (res.result.status === 'ok') {
          this.toastr.success('Model created successfully!');
        } else {
          this.toastr.error(res.error.message);
        }
      }), map(res => res.data));
  }

  updateModel(model: IModel) {
    return this.apiService.put<ModelResponse>(`models/${model.id}`, RestRequestType.UPDATE_MODEL, model)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Model updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  deleteModel(uuid: string) {
    return this.apiService.delete(`models/${uuid}`)
      .toPromise().then(res => {
        this.toastr.success('Model deleted successfully!');
        this.loadAllModels();
        return Promise.resolve();
      }, err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return Promise.reject(err.error);
      });
  }

  updateComponentInstanceName(payload: any) {
    return this.apiService.post(`component-instances/rename`, RestRequestType.UPDATE_COMPONENT_INSTANCE, payload)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Component instance updated successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  getModels(): Observable<ModelDetails[]> {
    return this.apiService.getGQLData({ query: getModelsList.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetModelsListInterface[] = response.data?.intellija_entities;
      const models = queryResponse.map(data => this.mapModelData(data));
      this._modelsSource.next(models || []);
      return models;
    }));
  }

  loadAllModels() {
    return this.apiService.getGQLData({ query: getModelsList.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetModelsListInterface[] = response.data?.intellija_entities;
      const models = queryResponse?.map(data => this.mapModelData(data));
      this._modelsSource.next(models || []);
      return models || [];
    }));
  }

  getModelById(id: string, isDashbaord: boolean = false) {
    return this.apiService.getGQLData({ query: isDashbaord ? getModelByIdForDashboards.query : getModelById.query, params: { id: id } }).pipe(
      catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), map((response) => {
        const queryResponse: GetModelByIdInterface[] = response.data?.intellija_entities;
        const models = queryResponse.map(data => this.mapModelData(data));
        return models || [];
      }), tap(models => {
        console.log('getModelById: ', models);
        this.dataService.tags.next(models[0]?.tags || []);
        this.dataService.alarms.next(models[0]?.alarms || []);
        this.dataService._properties.next(models[0]?.properties || []);
        this.dataService.modelComponents.next(models[0]?.components || []);
      }));
  }

  setModelDetails(id: string) {
    this.getModelById(id).subscribe(modelList => {
      if (modelList.length) {
        this._modelDetails.next(modelList[0]);
      }
    })
  }

  private mapModelData(model: GetModelsListInterface) {
    const alarms = getComponentInstAlarms(model.entitiesComponentsByEntityId || []);
    return {
      id: model.id,
      name: model.name,
      type: model.models?.length ? model.models[0]?.ref_model_types_id : null,
      notes: model.models?.length ? model.models[0]?.notes : '',
      hasSystemTags: model.models?.length ? model.models[0]?.has_system_tags : false,
      createdBy: model.created_by,
      createdOn: model.created_date,
      lastEditedBy: model.updated_by,
      lastEditedOn: model.updated_date,
      labels: this.getLabels(model.labels),
      tagsCount: this.getTagsCount(model.entitiesComponentsByEntityId),
      componentsCount: this.getComponentCount(model.entitiesComponentsByEntityId),
      tags: this.getTags(model.entitiesComponentsByEntityId),
      components: this.getComponenets(model.entitiesComponentsByEntityId),
      properties: this.getProperties(model),
      assets: model.entities || [],
      assetsCount: model.entities?.length || 0,
      alarms: alarms,
      alarmsCount: alarms.length || 0,
    };
  }

  private getComponenets(components: ComponentsByEntityId[]) {
    return components.reduce((componentList, component) => {
      const compId = component.component_id;
      const compList = component.model_component_instances.map(compInst => {
        // console.log('compInst: ', compInst);
        return {
          id: compId,
          name: compInst.entities_component?.entity?.name,
          componentInstanceId: compInst.component_instance_id,
          componentInstanceName: compInst.name,
          modelId: compInst.entities_component?.entityByEntityId?.id,
          modelName: compInst.entities_component?.entityByEntityId?.name,
          vendorName: compInst.entities_component?.entity?.components?.length ? compInst.entities_component?.entity?.components[0]?.vendor?.name : '',
          componentTypeName: compInst.entities_component?.entity?.components?.length ? compInst.entities_component?.entity?.components[0]?.ref_component_type?.name : ''
        }
      });

      return [...compList, ...componentList];
    }, []);
  }

  private getComponentCount(components: ComponentsByEntityId[]) {
    return components.reduce((count, item) => {
      return count + item.model_component_instances.length;
    }, 0);
  }

  getProperties(model: GetModelsListInterface) {
    const modelProperties = (model.properties || []).map(p => this.mapProperties(p, model.name, model.id));
    const componentProp = model.entitiesComponentsByEntityId.reduce((properties, component) => {
      const props = component.model_component_instances.reduce((result, compInst) => {
        const entityName = compInst.entities_component.entity?.name;
        const entityId = compInst.entities_component.entity?.id;
        const compInstProps =
          (compInst.entities_component.entity.properties || []).map(p => ({
            ...this.mapProperties(p, entityName, entityId),
            componentInstanceId: compInst.component_instance_id,
            componentInstanceName: compInst.name,
            entityType: EEntityTypes.COMPONENTS
          }));

        return [...compInstProps, ...result];
      }, [])

      return [...properties, ...props];
    }, []);

    return [...modelProperties, ...componentProp];
  }

  private mapProperties(property: PropertiesInterface, entityName: string, entityId: string) {
    return {
      id: property.id,
      name: property.name,
      parentPropertyId: property.parent_property_id,
      isOverride: property.is_override,
      value: property.value,
      dataTypeId: property.data_type,
      propertyType: property.ref_property_type?.name,
      action: null,
      createdBy: property.created_by,
      createdOn: property.created_date,
      lastEditedBy: property.updated_by,
      lastEditedOn: property.updated_date,
      entity_id: property.entity_id,
      entityName,
      entityId,
      // entityType: EEntityTypes.COMPONENTS,
      entityType: EEntityTypes.MODELS,
      disableDeleteAction: true
    }
  }

  /**
   * get all tags (cTags and mTags)
   * @param components components
   */
  getTags(components: ComponentsByEntityId[]): TagDetail[] {
    // console.log('components: ', components);
    let componentInstances: string = '';
    if (components?.length) {
      components.forEach(component => {
        componentInstances = componentInstances ? componentInstances + ', ' + component.model_component_instances[0]?.name : component.model_component_instances[0]?.name;
        // componentInstances = componentInstances ? '' : component.model_component_instances[0]?.name;
      });
    }

    const cTags = this.getModelTags(components, "entity", EEntityTypes.COMPONENTS);
    const mTags = this.getModelTags(components, "entityByEntityId", EEntityTypes.MODELS);
    let uniqueModelTags: TagDetail[] = [];
    if (mTags?.length) {
      const uniqueIds: string[] = [];
      uniqueModelTags = mTags.filter(mTag => {
        const isDuplicate = uniqueIds.includes(mTag.id);
        if (!isDuplicate) {
          uniqueIds.push(mTag.id);
          return true;
        }

        return false;
      });

      uniqueModelTags.forEach(uniqueModelTag => {
        if (uniqueModelTag.componentInstanceId) {
          uniqueModelTag.componentInstanceName = componentInstances;
        }
      });

      return [...cTags, ...uniqueModelTags];
    }

    return [...cTags, ...mTags];
  }

  /**
   * get tags individually
   * @param components components
   * @param entity entity
   * @param type type (components, models)
   */
  getModelTags(components: ComponentsByEntityId[], entity: string, type: string) {
    // console.log('components: ', components);
    return components.reduce((tags, component) => {
      const cTags = component.model_component_instances.reduce((instTags, comp) => {
        const componentName = comp.entities_component[entity]?.name;
        const entityId = comp.entities_component[entity]?.name;

        const tags = comp.entities_component[entity]?.tags?.map(tag => {
          const states = (tag.state_tags || []).map(s =>
          ({
            color: s.color,
            value: s.value,
            isBlink: s.is_blink,
            id: s.id,
            state: s.state
          }));

          return {
            id: tag.id,
            allowWrite: tag.allow_write || false,
            entity_id: tag.entity_id,
            name: tag.name,
            calc_tag_config: tag.calc_tag_config || null,
            tagTypeId: tag.tag_type_id,
            tagType: tag.ref_tag_type?.name,
            displayName: tag.display_name,
            description: tag.description,
            alarms: null,
            action: null,
            historizedId: tag.historized_id,
            displayEuId: tag.display_eu_id,
            displayEuName: tag.ref_uom?.name ?? '',
            displayEuSymbol: tag.ref_uom?.symbol ?? '',
            factor: tag.factor,
            offset: tag.offset,
            precision: tag.precision,
            rejectMax: tag.reject_max,
            rejectMin: tag.reject_min,
            histPercentChange: tag.hist_percent_change,
            isThousandSeparator: tag.is_thousand_separator || false,
            measurementCategory: tag.measurement_category,
            states,
            componentName,
            entityId,
            entityType: type,
            componentInstanceId: (type === EEntityTypes.MODELS) ? '' : comp.component_instance_id,
            componentInstanceName: (type === EEntityTypes.MODELS) ? '' : comp.name,
            disableDeleteAction: true,
            alarmsCount: tag.alarms_configs?.length || 0,
          }
        }) || [];

        return [...instTags, ...tags]
      }, []);

      return [...tags, ...cTags];
    }, []);
  }

  /**
   * get tags counts (ctags + mtags)
   * @param components components
   */
  getTagsCount(components: ComponentsByEntityId[]): number {
    const cTagsCount = this.getModelTagsCount(components, "entity");
    const mTagsCount = this.getModelTagsCount(components, "entityByEntityId");
    return cTagsCount + mTagsCount;
  }

  /**
   * get tags count individually
   * @param components components
   * @param entity entity
   */
  getModelTagsCount(components: ComponentsByEntityId[], entity: string): number {
    if (entity === 'entity') {
      return components.reduce((count, comp) => {
        const ctCount = comp.model_component_instances.reduce((result, component) => {
          return result += component.entities_component[entity]?.tags_aggregate?.aggregate?.count || 0;
        }, 0);
        return count + ctCount;
      }, 0);
    } else {
      const ctCount = components[0]?.model_component_instances[0]?.entities_component[entity]?.tags_aggregate?.aggregate?.count || 0;
      return ctCount;
    }
  }

  private getLabels(labels: Label[]): string[] {
    return labels.reduce((result, l) => {
      if (l.label?.name) {
        result.push(l.label.name);
      }

      return result;
    }, []);
  }

  getModelTypes() {
    return this.apiService.getGQLData({ query: getModelTypes.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => (response.data || [])));
  }

  /**
 * add model type
 * @param type type's name
 */
  addModelType(type: IAddRefTypes) {
    return this.apiService.post(`model-types`, RestRequestType.CREATE_MODEL_TYPE, type)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Model-Type created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

}